import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const SILVER_IMG = "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?auto=format&fit=crop&w=1400&q=80";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, error: "Missing reset token." });
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setState({ loading: false, ok: false, error: "Passwords do not match." });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/auth/reset-password", { token, password });
      setState({ loading: false, ok: true, error: "" });
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0a0a0b]" data-testid="reset-password-page">
      <div className="hidden md:block relative overflow-hidden border-r border-white/5 bg-black">
        <img src={SILVER_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0b]/40 via-transparent via-65% to-[#0a0a0b]" />
        <div className="absolute top-10 left-10"><Logo /></div>
        <div className="absolute bottom-12 left-10 right-10">
          <h2 className="font-display text-5xl text-white leading-[0.95] mb-3 tracking-tight">
            Forge a <em className="silver-text not-italic">new key</em>.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 relative">
        <div className="md:hidden absolute top-6 left-6"><Logo /></div>
        <div className="w-full max-w-md mt-12 md:mt-0">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">new key</div>
          <h1 className="font-display text-5xl md:text-6xl text-white mb-10 tracking-tight">Set password.</h1>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                New password <span className="lowercase tracking-normal text-zinc-600 normal-case">(min 6 chars)</span>
              </label>
              <input
                required
                type="password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-apex w-full px-4 py-3 font-body text-sm"
                data-testid="reset-password-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Confirm password
              </label>
              <input
                required
                type="password"
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input-apex w-full px-4 py-3 font-body text-sm"
                data-testid="reset-confirm-input"
                autoComplete="new-password"
              />
            </div>

            {state.error && (
              <div className="font-mono-apex text-xs text-[#dc2626]" data-testid="reset-error">{state.error}</div>
            )}
            {state.ok && (
              <div className="font-mono-apex text-xs text-emerald-400" data-testid="reset-success">
                Password reset. Redirecting to sign in…
              </div>
            )}

            <button
              type="submit"
              disabled={state.loading || !token}
              className="w-full btn-primary-apex px-6 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] disabled:opacity-60"
              data-testid="reset-submit-btn"
            >
              {state.loading ? "Updating…" : "Set new password"}
            </button>
          </form>

          <div className="mt-10 font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-700">
            ← <Link to="/login" className="hover:text-zinc-400">back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
